import React from 'react'
import { Badge, IconButton as MuiIconButton, IconButtonProps as MuiIconButtonProps, Tooltip } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface ToolbarIconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  icon: React.ReactNode
  label: string
  /** Numeric badge, e.g. unsaved-change count. Hidden when 0/undefined. */
  badge?: number
  active?: boolean
}

const Root = styled(MuiIconButton, {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  color: $active ? 'var(--ds-ink)' : 'var(--ds-ink-muted)',
  backgroundColor: $active ? 'var(--ds-hover)' : 'transparent',
  borderRadius: 'var(--ds-radius-pill)',
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover': {
    backgroundColor: 'var(--ds-hover)',
    color: 'var(--ds-ink)',
  },
  '&.Mui-disabled': {
    color: 'var(--ds-ink-subtle)',
    opacity: 0.5,
  },
}))

/** Icon button for editor top bars — optional dirty-count badge, active state */
export const ToolbarIconButton = React.forwardRef<HTMLButtonElement, ToolbarIconButtonProps>(
  function ToolbarIconButton({ icon, label, badge, active, ...rest }, ref) {
    const button = (
      <Root ref={ref} $active={active} aria-label={label} {...rest}>
        {badge ? (
          <Badge
            badgeContent={badge}
            color='error'
            sx={{
              '& .MuiBadge-badge': {
                fontSize: 10,
                fontWeight: 700,
                minWidth: 16,
                height: 16,
              },
            }}
          >
            {icon}
          </Badge>
        ) : (
          icon
        )}
      </Root>
    )
    return (
      <Tooltip title={label} arrow>
        <span>{button}</span>
      </Tooltip>
    )
  },
)
